'use client';

import { DestinationCGType, DestinationInputCGType } from '@/types/graphql/root';
import { saveDestinationMemo } from '@/utils/actions';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useEffect } from 'react';
import useActionHandler from '@/app/hooks/useActionHandler';
import { blackTransparentLoaderModalAtom } from '@/atom/modal';
import { useSetAtom } from 'jotai';

export default function MemoSaveBottomBar({
  tripId,
  destinations,
  setDestinations,
  memoDictionary,
}: {
  tripId: string;
  destinations: DestinationCGType[];
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  memoDictionary: {
    [key: string]: string;
  };
}) {
  const router = useRouter();
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const {
    execute: executeSaveDestinationMemo,
    data: saveDestinationMemoData,
    isLoading,
  } = useActionHandler({
    action: saveDestinationMemo,
    key: tripId,
  });

  const isChanged = useMemo(() => {
    const beforeMemo: { [key: string]: string } = {};
    destinations.forEach((destination) => {
      const destId = destination.id;
      if (!destId) return;
      beforeMemo[destId] = destination.memo ?? '';
    });

    return JSON.stringify(beforeMemo) !== JSON.stringify(memoDictionary);
  }, [memoDictionary, destinations]);

  const onClickSaveMemo = useCallback(async () => {
    if (!tripId) return;
    const beforeMemo: { [key: string]: string } = {};
    destinations.forEach((destination) => {
      const destId = destination.id;
      if (!destId) return;
      beforeMemo[destId] = destination.memo ?? '';
    });

    const changedMemos: { [key: string]: string } = {};
    Object.keys(memoDictionary).forEach((key) => {
      if (beforeMemo[key] !== memoDictionary[key]) {
        changedMemos[key] = memoDictionary[key];
      }
    });

    const destinationInputs: DestinationInputCGType[] = [];
    for (const destId in changedMemos) {
      const _dest = destinations.find((_d) => _d.id === destId);
      if (!_dest) continue;
      destinationInputs.push({
        id: destId,
        accommType: _dest.accommType,
        day: _dest.day,
        pos: _dest.pos,
        placeId: _dest.poi?.placeId,
        type: _dest.type,
        memo: changedMemos[destId],
      });
    }

    await executeSaveDestinationMemo(tripId, destinationInputs);
  }, [tripId, destinations, memoDictionary]);

  useEffect(() => {
    if (saveDestinationMemoData) {
      setDestinations((prev) => {
        return prev.map((_d) => {
          const updatedDest = saveDestinationMemoData.find((_di) => _di?.id === _d.id);
          if (!updatedDest) return _d;
          return {
            ..._d,
            memo: updatedDest.memo,
          };
        });
      });
      router.back();
    }
  }, [saveDestinationMemoData]);

  useEffect(() => {
    setBlackTransparentLoaderModal(isLoading);
  }, [isLoading]);

  return (
    <div
      className='max-w-[30rem] fixed bottom-0 z-10 w-full bg-white h-100pxr rounded-t-16pxr px-20pxr pt-16pxr pb-34pxr flex gap-8pxr'
      style={{
        boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
      }}
    >
      <button
        className='px-16pxr py-14pxr border border-[#3177FF] rounded-8pxr flex items-center justify-center text-[#3177FF] w-full button-16-m gap-2pxr'
        onClick={() => {
          router.back();
        }}
      >
        취소하기
      </button>
      <button
        className={`px-16pxr py-14pxr ${
          !isChanged ? 'bg-[#DEE0E1]' : 'bg-[#3177FF]'
        } rounded-8pxr flex items-center justify-center text-white w-full button-16-m`}
        disabled={!isChanged}
        onClick={() => onClickSaveMemo()}
      >
        메모 저장
      </button>
    </div>
  );
}
